import {
  getItemFromLocalStorage,
  setItemInLocalStorage
} from 'utils/localStorage'
import { IAvatarData } from 'store/types/aiAvatar'

// Persists source-image Enhance/Resize state across page reloads (the socket
// correlation lives only in redux, and missed `type:'ai-image'` events are
// never replayed — INV-AIMG-1), in two independent shapes:
//
// CASE 1 — FRESH form (no targetMediaItemId): one slot. A fresh form can have
// at most one op in flight (the loader blocks the action buttons), and the
// form itself isn't rebuildable, so the entry snapshots the avatar too.
// Lifecycle: written at kickoff (enhance/resize thunks) → removed at every
// terminal — socket success/error, kickoff failure, and every deliberate
// abandon (new image picked, avatar deleted, form replaced/reset). On AI
// Avatar tab mount, `restorePendingSourceImage` (thunks/sourceImage.ts)
// validates + reseeds the form from this entry and reconciles against the
// backend.
//
// CASE 2 — REGEN form (targetMediaItemId set): a map keyed by the clip's
// `assetId` (globally-unique avatar project id — survives reselect and clip
// duplication; a regeneration re-keys the clip to a NEW assetId, so stale
// entries orphan and get pruned). Each entry holds the in-flight op and/or
// the latest finished result — the regen form is rebuilt from the backend on
// every (re)select, and the backend still holds the ORIGINAL reference image,
// so the result must outlive the form. Unlike case 1, deselecting the clip
// does NOT abandon the op: the map's only terminals are socket success/error,
// the reconcile backstop, the Undo write-back, and TTL/cap pruning.

const STORAGE_KEY = 'aiAvatarPendingSourceImage'
const ASSET_OPS_STORAGE_KEY = 'aiAvatarSourceImageOpsByAsset'
// Result entries are per-browser breadcrumbs; cap the map (oldest dropped) so
// it can't grow unbounded across projects and months.
const MAX_ASSET_OP_ENTRIES = 30

// An entry older than this is considered dead (backend never answered) and is
// dropped at restore instead of resurrecting a forever-loader.
export const PENDING_SOURCE_IMAGE_TTL_MS = 15 * 60 * 1000

export interface IPendingSourceImageEntry {
  videoProjectId: number
  // The ai-image project id the `type:'ai-image'` socket / user-images-by-ids
  // reconcile fetch correlate on.
  imageId: number
  // Resize's target ratio to commit on completion; null for Enhance (keeps the
  // current ratio). The socket message doesn't carry it, so it must be stored.
  targetAspectRatio: string | null
  // The form's avatar at kickoff — after a reload the fresh form is empty, so
  // the preview (loader over the current image), the `source`-driven behavior,
  // and the post-completion undo snapshot all need this snapshot back.
  avatar: IAvatarData
  // generationParams.aspectRatio at kickoff (the avatar's ratio override).
  ratioOverride: string | null
  createdAt: number
}

export const savePendingSourceImageEntry = (
  entry: IPendingSourceImageEntry
): void => {
  setItemInLocalStorage(STORAGE_KEY, entry)
}

export const removePendingSourceImageEntry = (): void => {
  localStorage.removeItem(STORAGE_KEY)
}

// Shape-validated read: localStorage is user-editable and survives deploys, so
// a malformed entry is treated as absent (and removed) instead of throwing.
export const getPendingSourceImageEntry =
  (): IPendingSourceImageEntry | null => {
    try {
      const entry = getItemFromLocalStorage(
        STORAGE_KEY
      ) as IPendingSourceImageEntry | null
      if (
        !entry ||
        typeof entry.videoProjectId !== 'number' ||
        typeof entry.imageId !== 'number' ||
        typeof entry.createdAt !== 'number' ||
        typeof entry.avatar?.url !== 'string' ||
        typeof entry.avatar?.source !== 'string'
      ) {
        if (entry) removePendingSourceImageEntry()
        return null
      }
      return entry
    } catch {
      removePendingSourceImageEntry()
      return null
    }
  }

// ---------------------------------------------------------------------------
// Case 2 — per-clip (assetId-keyed) ops map
// ---------------------------------------------------------------------------

export interface IAssetPendingSourceOp {
  // The ai-image project id the socket / reconcile fetch correlate on.
  imageId: number
  // Resize's target ratio to commit on completion; null for Enhance.
  targetAspectRatio: string | null
  createdAt: number
}

export interface IAssetSourceImageResult {
  url: string
  width: number
  height: number
  // The ratio the finished op committed (Resize target; null = kept, Enhance).
  aspectRatio: string | null
  createdAt: number
}

export interface IAssetSourceImageOp {
  pending?: IAssetPendingSourceOp
  result?: IAssetSourceImageResult
}

type TAssetOpsMap = Record<string, IAssetSourceImageOp>

const isValidPending = (p: IAssetPendingSourceOp | undefined): boolean =>
  !!p && typeof p.imageId === 'number' && typeof p.createdAt === 'number'

const isValidResult = (r: IAssetSourceImageResult | undefined): boolean =>
  !!r &&
  typeof r.url === 'string' &&
  typeof r.width === 'number' &&
  typeof r.height === 'number' &&
  typeof r.createdAt === 'number'

const entryTimestamp = (op: IAssetSourceImageOp): number =>
  Math.max(op.pending?.createdAt ?? 0, op.result?.createdAt ?? 0)

// Read + sanitize in one pass: malformed halves are dropped, a pending op past
// its TTL is dead (its socket event is long gone — same ceiling as case 1),
// and the map is capped to the newest MAX_ASSET_OP_ENTRIES entries.
const readAssetOpsMap = (): TAssetOpsMap => {
  let raw: unknown
  try {
    raw = getItemFromLocalStorage(ASSET_OPS_STORAGE_KEY)
  } catch {
    return {}
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}

  const now = Date.now()
  const sanitized: TAssetOpsMap = {}
  for (const [assetId, op] of Object.entries(raw as TAssetOpsMap)) {
    if (!op || typeof op !== 'object') continue
    const pending =
      isValidPending(op.pending) &&
      now - op.pending!.createdAt <= PENDING_SOURCE_IMAGE_TTL_MS
        ? op.pending
        : undefined
    const result = isValidResult(op.result) ? op.result : undefined
    if (pending || result) sanitized[assetId] = { pending, result }
  }

  const entries = Object.entries(sanitized)
  if (entries.length <= MAX_ASSET_OP_ENTRIES) return sanitized
  return Object.fromEntries(
    entries
      .sort(([, a], [, b]) => entryTimestamp(b) - entryTimestamp(a))
      .slice(0, MAX_ASSET_OP_ENTRIES)
  )
}

const writeAssetOpsMap = (map: TAssetOpsMap): void => {
  setItemInLocalStorage(ASSET_OPS_STORAGE_KEY, map)
}

export const getAssetOp = (assetId: string): IAssetSourceImageOp | null =>
  readAssetOpsMap()[assetId] ?? null

// Kickoff: remember the in-flight op for this clip. A previous result is kept
// until the new op finishes (it stays valid if the new op fails).
export const saveAssetPendingOp = (
  assetId: string,
  pending: IAssetPendingSourceOp
): void => {
  const map = readAssetOpsMap()
  map[assetId] = { ...map[assetId], pending }
  writeAssetOpsMap(map)
}

const findEntryByPendingImageId = (
  map: TAssetOpsMap,
  imageId: number
): [string, IAssetSourceImageOp] | undefined =>
  Object.entries(map).find(([, op]) => op.pending?.imageId === imageId)

// Completion: convert the matching pending op into this clip's latest result
// (the committed ratio comes from the pending op — the socket message doesn't
// carry it). No-op for unknown ids (e.g. a case-1 op, which has no map entry).
// Returns whether an op was matched, so the socket handler can consume the
// message for closed-form completions.
export const storeAssetResultByImageId = (
  imageId: number,
  result: Omit<IAssetSourceImageResult, 'aspectRatio' | 'createdAt'>
): boolean => {
  const map = readAssetOpsMap()
  const entry = findEntryByPendingImageId(map, imageId)
  if (!entry) return false
  const [assetId, op] = entry
  map[assetId] = {
    result: {
      ...result,
      aspectRatio: op.pending!.targetAspectRatio,
      createdAt: Date.now()
    }
  }
  writeAssetOpsMap(map)
  return true
}

// Failure terminal: drop the pending op (a previous result, if any, survives).
// Returns whether an op was matched (see storeAssetResultByImageId).
export const clearAssetPendingByImageId = (imageId: number): boolean => {
  const map = readAssetOpsMap()
  const entry = findEntryByPendingImageId(map, imageId)
  if (!entry) return false
  const [assetId, op] = entry
  if (op.result) {
    map[assetId] = { result: op.result }
  } else {
    delete map[assetId]
  }
  writeAssetOpsMap(map)
  return true
}

// Undo write-back: the user restored the original, so the stored result must
// not resurrect on the next reselect.
export const removeAssetOp = (assetId: string): void => {
  const map = readAssetOpsMap()
  if (!(assetId in map)) return
  delete map[assetId]
  writeAssetOpsMap(map)
}
